import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { SearchFilters } from "@/components/search/SearchFilters";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { slugToCategory, categoryToSlug } from "@/lib/categories";
import { toast } from "sonner";
import api from "@/lib/api";

const PAGE_SIZE = 24;

const CATEGORY_INTRO = {
  Motors: "Browse cars, vans, motorbikes and commercials for sale from private sellers and dealers right across Ireland.",
  Jobs: "Find local jobs or hire staff across Ireland, from trades and construction to hospitality, retail and office roles.",
  "House & DIY": "Furniture, appliances, power tools and everything you need for the home and garden, all close to you.",
  Farming: "Tractors, machinery, livestock equipment and farm supplies for the Irish agricultural community.",
  Lettings: "Apartments, houses, rooms and shared accommodation to rent across all 26 counties of Ireland.",
  "Property for Sale": "Houses, apartments, new homes and sites for sale nationwide, listed by owners and agents.",
  "Clothes & Fashion": "New and pre-loved clothes, shoes and accessories for men, women and kids at fair local prices.",
  "Sports & Leisure": "Bikes, gym gear, golf, camping and sports equipment to buy and sell near you.",
};

export default function SearchPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { slug } = useParams();
  const slugCategory = slug ? slugToCategory(slug) : null;
  const [cats, setCats] = useState({});
  const [counties, setCounties] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(slugCategory || params.get("category") || "all");
  const [subcategory, setSubcategory] = useState(params.get("subcategory") || "all");
  const [county, setCounty] = useState(params.get("county") || "all");
  const [area, setArea] = useState(params.get("area") || "");
  const [condition, setCondition] = useState(params.get("condition") || "all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [sort, setSort] = useState("newest");
  const sellerId = params.get("seller_id") || "";
  const isProperty = ["Lettings", "Property for Sale"].includes(category);

  useEffect(() => {
    api.get("/categories").then((r) => { setCats(r.data.categories); setCounties(r.data.counties); });
  }, []);

  useEffect(() => {
    if (slugCategory) setCategory(slugCategory);
  }, [slugCategory]);

  const buildParams = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (category !== "all") p.category = category;
    if (subcategory !== "all") p.subcategory = subcategory;
    if (county !== "all") p.county = county;
    if (area) p.area = area;
    if (condition !== "all") p.condition = condition;
    if (minPrice) p.min_price = minPrice;
    if (maxPrice) p.max_price = maxPrice;
    if (minBedrooms && ["Lettings", "Property for Sale"].includes(category)) p.min_bedrooms = minBedrooms;
    if (sellerId) p.seller_id = sellerId;
    p.sort = sort;
    return p;
  }, [q, category, subcategory, county, area, condition, minPrice, maxPrice, minBedrooms, sellerId, sort]);

  const saveSearch = async () => {
    const p = buildParams();
    const label = q || [category !== "all" ? category : null, county !== "all" ? county : null, area].filter(Boolean).join(" · ") || "All ads";
    const name = window.prompt("Name this saved search:", label);
    if (name === null) return;
    try {
      await api.post("/saved-searches", { name: name.trim() || label, query: p });
      toast.success("Search saved, find it in your dashboard");
    } catch {
      toast.error("Could not save search. Please try again.");
    }
  };

  const clearAll = () => { setCategory("all"); setSubcategory("all"); setCounty("all"); setArea(""); setCondition("all"); setMinPrice(""); setMaxPrice(""); setQ(""); };

  const fetchListings = useCallback(() => {
    setLoading(true);
    const p = { ...buildParams(), limit: PAGE_SIZE, skip: 0 };
    api.get("/listings", { params: p })
      .then((r) => { setListings(r.data); setHasMore(r.data.length === PAGE_SIZE); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    const p = { ...buildParams(), limit: PAGE_SIZE, skip: listings.length };
    api.get("/listings", { params: p })
      .then((r) => { setListings((prev) => [...prev, ...r.data]); setHasMore(r.data.length === PAGE_SIZE); })
      .finally(() => setLoadingMore(false));
  }, [buildParams, listings.length]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const subList = category !== "all" && cats[category] ? cats[category].subcategories : [];

  const hasCategory = category !== "all";
  const pageTitle = hasCategory ? `${category} in Ireland | DealShed` : "Search classified ads across Ireland | DealShed";
  const h1Text = hasCategory ? `${category} in Ireland` : "Search DealShed";
  useSeo({
    title: pageTitle,
    description: hasCategory
      ? `${CATEGORY_INTRO[category] || `Browse ${category} ads on DealShed.`} First ad free, then just €5.`
      : "Search thousands of classified ads across Ireland on DealShed, from motors and jobs to property, farming and fashion.",
    canonical: hasCategory ? `${window.location.origin}/category/${categoryToSlug(category)}` : `${window.location.origin}/search`,
  });

  const filters = {
    show: showFilters, cats, counties, subList, isProperty, user,
    category, setCategory, subcategory, setSubcategory, county, setCounty,
    area, setArea, condition, setCondition, minPrice, setMinPrice,
    maxPrice, setMaxPrice, minBedrooms, setMinBedrooms, clearAll, saveSearch,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex-1">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900" data-testid="search-heading">{h1Text}</h1>
        {hasCategory && CATEGORY_INTRO[category] && (
          <p className="text-sm text-slate-600 mt-1 mb-4 max-w-3xl" data-testid="category-intro">{CATEGORY_INTRO[category]}</p>
        )}
        {/* search bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 mt-4">
          <div className="flex-1 flex items-center gap-2 border border-slate-300 rounded-md px-3 bg-white">
            <SearchIcon size={18} className="text-slate-400" />
            <input data-testid="search-page-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search DealShed..." className="w-full py-2.5 outline-none" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Mobile filters toggle */}
        <button data-testid="mobile-filters-toggle" onClick={() => setShowFilters((s) => !s)}
          className="lg:hidden w-full flex items-center justify-center gap-2 border border-slate-300 rounded-md py-2.5 mb-4 font-medium text-slate-700">
          <SlidersHorizontal size={16} /> {showFilters ? "Hide filters" : "Show filters"}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <SearchFilters f={filters} />

          {/* Results */}
          <main className="lg:col-span-3">
            <p className="text-sm text-slate-500 mb-4" data-testid="results-count">{loading ? "Searching..." : `${listings.length}${hasMore ? "+" : ""} ads found`}</p>
            {listings.length === 0 && !loading ? (
              <div className="text-center py-20 text-slate-400" data-testid="no-results">No listings match your filters.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
                </div>
                {hasMore && !loading && (
                  <div className="flex justify-center mt-8">
                    <Button data-testid="load-more" variant="outline" disabled={loadingMore} onClick={loadMore} className="px-8">
                      {loadingMore ? "Loading..." : "Load more"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
